import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Container, Btn } from '../components/ui';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { articleJsonLd, breadcrumbJsonLd } from '../data/seo';
import SocialShare from '../components/SocialShare';
import CTASection from '../components/CTASection';
import { BlogCard } from '../components/cards';
import { BlogArticle } from '../components/ArticleView';
import { fetchBlog, fetchBlogs, resolveImg, trackContent } from '../lib/marketingApi';

export default function BlogPost() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const [post, setPost] = useState(undefined);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setPost(undefined);
    fetchBlog(slug).then((d) => setPost(d.item)).catch(() => setPost(null));
    fetchBlogs().then((d) => setRelated((d.items || []).filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (post && post.slug) {
      const key = `bpv:blog:${post.slug}`;
      if (!sessionStorage.getItem(key)) { trackContent('blog', post.slug, 'view'); sessionStorage.setItem(key, '1'); }
    }
  }, [post]);

  if (post === undefined) {
    return <div className="flex items-center justify-center py-40 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (post === null) return <Navigate to="/blog" replace />;

  return (
    <div>
      <Seo
        title={post.seo?.meta_title || post.title}
        description={post.seo?.meta_description || post.excerpt}
        keywords={post.seo?.keywords}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image ? resolveImg(post.image) : undefined}
        jsonLd={[
          articleJsonLd({ title: post.title, description: post.excerpt, image: post.image ? resolveImg(post.image) : undefined, path: `/blog/${post.slug}`, datePublished: post.published_at || post.date, dateModified: post.updated_at }),
          breadcrumbJsonLd([{ name: 'Blog', path: '/blog' }, { name: post.title, path: `/blog/${post.slug}` }]),
        ]}
      />
      <div className="px-5 pt-8 sm:px-8">
        <Container className="max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand dark:text-slate-400"><ArrowLeft className="h-4 w-4" /> {t('common.allArticles')}</Link>
        </Container>
      </div>

      <BlogArticle post={post} />

      <section className="px-5 pb-4 sm:px-8" data-testid="blog-signup-cta">
        <Container className="max-w-3xl">
          <div className="relative overflow-hidden rounded-2xl bg-[#0035A8] px-6 py-8 text-white sm:px-10 sm:py-10">
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-12 left-1/3 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
            <div className="relative flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-display text-2xl font-bold leading-tight sm:text-3xl">Signup today to get started for Free</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/80 sm:text-base">Automate your utility bill management with BillPortal — no credit card required.</p>
              </div>
              <a
                href="https://billportal.ai/signup"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-signup-btn"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0035A8] shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-slate-50"
              >
                Get started free <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Container>
      </section>

      <section className="px-5 pb-12 sm:px-8">
        <Container className="max-w-3xl">
          <div className="flex flex-col items-start justify-between gap-4 border-t border-slate-200 pt-6 dark:border-white/10 sm:flex-row sm:items-center">
            <span onClick={() => trackContent('blog', slug, 'share_click')}><SocialShare title={post.title} summary={post.excerpt} /></span>
            <span onClick={() => trackContent('blog', slug, 'cta_click')} data-testid="article-cta-demo"><Btn to="/demo" arrow>{t('common.requestDemo')}</Btn></span>
          </div>
        </Container>
      </section>

      {related.length > 0 && (
        <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-20">
          <Container>
            <h2 className="mb-8 font-display text-2xl font-bold text-slate-900 dark:text-white">{t('common.allArticles')}</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 60}>
                  <div onClick={() => trackContent('blog', slug, 'related_click')}><BlogCard post={{ ...p, image: resolveImg(p.image) }} /></div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      <CTASection />
    </div>
  );
}
